import React from 'react'
import { BookOpen } from 'lucide-react'
import { sage, neutral, fonts } from '../styles/theme.js'

const API_URL = import.meta.env.VITE_API_URL || ''

export default function SignIn() {
  const handleGoogle   = () => { window.location.href = `${API_URL}/auth/google` }
  const handleFacebook = () => { window.location.href = `${API_URL}/auth/facebook` }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      background: `linear-gradient(150deg, ${sage.faint} 0%, #ffffff 50%, ${sage.pale} 100%)`,
    }}>
      {/* Left decorative panel */}
      <div style={{
        width: 480, flexShrink: 0, background: sage.dark,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: 56, position: 'relative', overflow: 'hidden',
      }} className="sign-in-panel">
        {/* Subtle circle decorations */}
        <div style={{ position: 'absolute', top: -80, right: -80, width: 280, height: 280, borderRadius: '50%', background: `${sage.mid}30` }} />
        <div style={{ position: 'absolute', bottom: -60, left: -60, width: 200, height: 200, borderRadius: '50%', background: `${sage.mid}20` }} />

        <BookOpen size={52} color="white" style={{ marginBottom: 28, opacity: 0.9 }} />
        <h1 style={{
          fontFamily: fonts.cursive, fontSize: 48, fontWeight: 700,
          color: 'white', marginBottom: 12, textAlign: 'center', lineHeight: 1.1,
        }}>
          My Cookbook
        </h1>
        <p style={{
          fontFamily: fonts.serif, fontSize: 16, color: `${sage.pale}cc`,
          textAlign: 'center', lineHeight: 1.7, maxWidth: 300,
        }}>
          Your family's recipes, beautifully organised and always within reach.
        </p>

        {/* Feature list */}
        <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', gap: 14, width: '100%', maxWidth: 280 }}>
          {['Timers & unit conversions', 'AI cooking assistant', 'Import from any website'].map(f => (
            <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: sage.light, flexShrink: 0 }} />
              <span style={{ fontFamily: fonts.serif, fontSize: 15, color: `${sage.pale}dd` }}>{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right sign-in panel */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 40,
      }}>
        <div style={{ width: '100%', maxWidth: 380, animation: 'fadeIn 0.5s ease' }}>
          <h2 style={{
            fontFamily: fonts.cursive, fontSize: 36, fontWeight: 700,
            color: neutral[900], marginBottom: 6,
          }}>
            Welcome back
          </h2>
          <p style={{
            fontFamily: fonts.serif, fontSize: 15, color: neutral[500], marginBottom: 36,
          }}>
            Sign in to access your recipes
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button onClick={handleGoogle} style={oauthBtn('#ffffff', neutral[900], neutral[300])}>
              <GoogleIcon />
              <span>Continue with Google</span>
            </button>
            <button onClick={handleFacebook} style={oauthBtn('#1877F2', '#ffffff', '#1877F2')}>
              <FacebookIcon />
              <span>Continue with Facebook</span>
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '28px 0' }}>
            <div style={{ flex: 1, height: 1, background: neutral[300] }} />
            <span style={{ fontFamily: fonts.serif, fontSize: 13, color: neutral[500] }}>
              Secure OAuth sign-in
            </span>
            <div style={{ flex: 1, height: 1, background: neutral[300] }} />
          </div>

          <p style={{
            fontFamily: fonts.serif, fontSize: 13, color: neutral[500],
            textAlign: 'center', lineHeight: 1.6,
          }}>
            By signing in you agree to our{' '}
            <a href="/privacy" style={{ color: sage.mid, textDecoration: 'none' }}>Privacy Policy</a>.
            <br />We never see your password.
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 700px) {
          .sign-in-panel { display: none !important; }
        }
      `}</style>
    </div>
  )
}

function oauthBtn(bg, color, border) {
  return {
    width: '100%', padding: '13px 20px',
    background: bg, color, border: `1.5px solid ${border}`,
    borderRadius: 10, cursor: 'pointer',
    fontFamily: fonts.serif, fontSize: 16, fontWeight: 500,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    transition: 'box-shadow 0.15s, transform 0.15s',
  }
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

function FacebookIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  )
}
